import { Button } from "components/DesignSystem/Button/Button";
import { SelectedFloatingControl } from "components/SelectedFloatingControl/SelectedFloatingControl";
import { Cross } from "components/icons/Cross";
import { DividerLine } from "components/icons/DividerLine";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useBulkDeleteCustomersMutation } from "store/apis/invoices";
import { BackendError } from "types/utils/error";
import { pluralize } from "utils/pluralize";
import { BulkDeleteCustomersModal } from "./BulkDeleteCustomersModal";

type CustomerBulkActionsProps = {
  selectedCustomerIds: string[];
  onClearSelection: () => void;
  entityId: string;
  groupId: string;
};

export const CustomerBulkActions = ({
  selectedCustomerIds,
  onClearSelection,
  groupId,
  entityId,
}: CustomerBulkActionsProps) => {
  const deleteModal = useModal();
  const { successToast, warning, alertToast } = useToast();
  const [bulkDeleteCustomers, { isLoading }] = useBulkDeleteCustomersMutation();

  const selectedCount = selectedCustomerIds.length;

  const showResultToast = (deletedCount: number, skippedCount: number) => {
    if (skippedCount === 0) {
      successToast({
        message: `${pluralize(deletedCount, "customer", "customers")} deleted`,
      });
      return;
    }

    if (deletedCount > 0) {
      warning({
        message: `${deletedCount} deleted, ${skippedCount} skipped (Stripe customers can't be deleted)`,
      });
      return;
    }

    alertToast({ message: "Stripe customers can't be deleted" });
  };

  const handleDelete = async () => {
    try {
      const { deleted_customer_ids, skipped_customer_ids } =
        await bulkDeleteCustomers({
          groupId,
          entityId,
          customerIds: selectedCustomerIds,
        }).unwrap();

      showResultToast(deleted_customer_ids.length, skipped_customer_ids.length);
      deleteModal.close();
      onClearSelection();
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  if (selectedCount === 0) return null;

  return (
    <>
      <SelectedFloatingControl>
        <>
          <span className="t-whitespace-nowrap t-text-subtext t-text-text-60">
            {selectedCount} selected
          </span>
          <span className="t-text-neutral-10 t-h-full">
            <DividerLine />
          </span>
          <Button size="small" customType="danger" onClick={deleteModal.open}>
            Delete
          </Button>
          <Button
            customType="ghost_icon"
            size="xsmall"
            onClick={onClearSelection}
            aria-label="Clear selection"
          >
            <Cross />
          </Button>
        </>
      </SelectedFloatingControl>

      <BulkDeleteCustomersModal
        open={deleteModal.isOpen}
        count={selectedCount}
        isLoading={isLoading}
        onCancel={deleteModal.close}
        onConfirm={handleDelete}
      />
    </>
  );
};
